import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { encrypt, decrypt } from "../../utils/encryption";
import { formatCurrency } from "../../utils/currency";
import { formatDate } from "../../utils/dateUtils";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import Modal from "../../components/common/Modal";
import CrudModal from "../../components/common/crud/CrudModal";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import purchaseMasterService from "../../services/purchaseMasterService";
import stockCategoryService from "../../services/stockCategoryService";
import lotService from "../../services/lotService";
import yardService from "../../services/yardService";
import blService from "../../services/blService";
import alertService from "../../utils/alertService";
import LotCreateModal from "../Modals/LotCreateModal";
import YardCreateModal from "../Modals/YardCreateModal";

const convertStockSchema = [
  {
    name: "lotNumber",
    label: "Lot Number",
    type: "searchableDropdown",
    placeholder: "Select Lot",
    options: [],
    validation: { required: true },
  },
  {
    name: "date",
    label: "Arrival Date",
    type: "date",
    validation: { required: true, notFuture: true },
  },
  {
    name: "yardId",
    label: "Yard",
    type: "searchableDropdown",
    placeholder: "Select Yard",
    options: [],
    validation: { required: true },
  },
];

function BLContainerModulePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const encryptedPmId = searchParams.get("pmId");
  const pmIdFromUrl = useMemo(() => decrypt(encryptedPmId), [encryptedPmId]);

  const [stockCategoryOptions, setStockCategoryOptions] = useState([]);
  const [lotOptions, setLotOptions] = useState([]);
  const [purchaseOptions, setPurchaseOptions] = useState([]);
  const [blMasters, setBlMasters] = useState([]);
  const [yardOptions, setYardOptions] = useState([]);
  const [containerOptions, setContainerOptions] = useState([]);

  // State for View Detail Mode
  const [viewDetailData, setViewDetailData] = useState(null);
  const [viewDetailStock, setViewDetailStock] = useState([]);
  const [isLoadingView, setIsLoadingView] = useState(false);

  // Convert Stock Modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedStockForConvert, setSelectedStockForConvert] = useState(null);
  const [isEditConvert, setIsEditConvert] = useState(false);

  // New Modals
  const [lotModalOpen, setLotModalOpen] = useState(false);
  const [yardModalOpen, setYardModalOpen] = useState(false);

  const loadOptions = useCallback(async () => {
    try {
      const [categories, lots, purchases, bls, yards] = await Promise.all([
        stockCategoryService.getActiveStockCategories(),
        lotService.getActiveLots(),
        purchaseMasterService.getActivePurchaseMasters(),
        blService.getActiveBLs(),
        yardService.getActiveYards(),
      ]);

      setStockCategoryOptions(
        (categories?.items || categories?.data || []).map((s) => ({
          label: s.stockCategoryName,
          value: s.stockCategoryId,
        })),
      );
      setLotOptions(
        (lots?.items || []).map((l) => ({
          label: l.lotNo,
          value: l.lotId,
        })),
      );
      setPurchaseOptions(
        (purchases?.items || purchases?.data || []).map((p) => ({
          label: p.billNo,
          value: p.purchaseMasterId || p.id,
          dollarRate: p.dollarRate,
          currencyCode: p.currency?.currencyCode || p.currencyCode || "USD",
        })),
      );
      setBlMasters(bls?.items || []);
      setYardOptions(
        (yards?.items || yards?.data || []).map((y) => ({
          label: y.yardNo,
          value: y.yardId,
        })),
      );
    } catch (err) {
      console.error("Options failed", err);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const endpoints = useMemo(
    () => ({
      list: "/container-stock-details",
      delete: (id) => `/container-stock-details/${id}`,
    }),
    [],
  );

  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "blContainerStockDetailsId",
    initialFilters: {
      search: "",
      stockCategoryId: "",
      pmId: pmIdFromUrl || "",
      blContainerId: "",
    },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  useEffect(() => {
    const fetchContainers = async () => {
      if (!filters.pmId) {
        setContainerOptions([]);
        return;
      }
      try {
        const res = await apiClient.get("/containers", {
          params: { pmId: filters.pmId, limit: 100 },
        });
        const data = res?.data?.data || res?.data || [];
        const items = Array.isArray(data) ? data : (data.items || []);
        setContainerOptions(
          items.map((c) => ({
            label: c.containerNo,
            value: String(c.blContainerId),
          })),
        );
      } catch (err) {
        console.error("Failed to fetch containers", err);
      }
    };
    fetchContainers();
  }, [filters.pmId]);

  const getArrivedStatus = useCallback(
    (item) => {
      const pmId = item?.blContainer?.pmId || item?.pmId;
      if (!pmId) return { isArrived: false, status: "Unknown" };

      const bl = blMasters.find((b) => String(b.pmId) === String(pmId));
      if (!bl) return { isArrived: false, status: "No BL" };

      const arrivedStatuses = ["Arrived", "Arrived ICD"];
      return {
        isArrived: arrivedStatuses.includes(bl.status),
        status: bl.status,
      };
    },
    [blMasters],
  );

  const tableColumns = useMemo(
    () => [
      {
        key: "billNo",
        label: "Bill No",
        sortable: true,
        render: (item) => {
          const pmId = item?.blContainer?.pmId || item?.pmId;
          const match = purchaseOptions.find(
            (opt) => String(opt.value) === String(pmId),
          );
          return match ? String(match.label) : "-";
        },
      },
      {
        key: "containerNo",
        label: "Container No",
        sortable: true,
        className: "font-bold text-slate-700 dark:text-slate-200",
        render: (item) => String(item?.blContainer?.containerNo || "-"),
      },
      {
        key: "stockCategoryName",
        label: "Stock Category",
        sortable: false,
        render: (item) => {
          const raw = item?.stockCategory?.stockCategoryName || item?.stockCategoryName || "-";
          const categories = String(raw)
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean);

          if (categories.length === 0) return "-";
          if (categories.length === 1) return String(categories[0]);

          return (
            <div className="flex flex-wrap gap-1">
              {categories.map((category, index) => (
                <span
                  key={`${category}-${index}`}
                  className="inline-flex items-center rounded-full bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  {category}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: "lotNo",
        label: "Lot No",
        sortable: true,
        render: (item) => {
          const lotLabel = item.lotNo || item.lot?.lotNo;
          return lotLabel ? (
            <span className="inline-flex items-center rounded bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-2 py-0.5 text-[10px] font-black text-slate-600 dark:text-slate-400">
              {lotLabel}
            </span>
          ) : "-";
        },
      },
      {
        key: "yardName",
        label: "Yard",
        sortable: true,
        render: (item) => (
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">
            {item.yardName || item.yard?.yardNo || "-"}
          </span>
        ),
      },
      {
        key: "netWeight",
        label: "Net Weight (KG)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-mono font-bold text-primary-600",
        render: (item) =>
          item?.netWeight ? Number(item.netWeight).toLocaleString() : "0",
      },
      {
        key: "price",
        label: (
          <div className="flex flex-col items-end min-h-[32px] justify-end">
            <span>Price/MT</span>
            <span className="text-[8px] font-medium text-slate-400 normal-case leading-tight italic">
              (Dynamic Currency)
            </span>
          </div>
        ),
        sortable: false,
        headerClassName: "text-right",
        className: "text-right font-mono font-bold",
        render: (item) =>
          item.price
            ? Number(item.price).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            : "-",
      },
      {
        key: "totalPrice",
        label: (
          <div className="flex flex-col items-end min-h-[32px] justify-end">
            <span>Total Price </span>
            <span className="text-[8px] font-medium text-slate-400 normal-case leading-tight">
              (Price * Weight) / 1000
            </span>
          </div>
        ),
        sortable: false,
        headerClassName: "text-right",
        className: "text-right font-mono font-black text-emerald-600",
        render: (item) => {
          const total =
            (parseFloat(item.price) * parseFloat(item.netWeight)) / 1000;
          return total.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
      {
        key: "priceInRS",
        label: (
          <div className="flex flex-col items-end min-h-[32px] justify-end">
            <span>Price (RS)</span>
            <span className="text-[8px] font-medium text-slate-400 normal-case leading-tight">
              (Price * Weight * Rate) / 1000
            </span>
          </div>
        ),
        sortable: false,
        headerClassName: "text-right",
        className: "text-right font-mono font-bold text-slate-700 dark:text-slate-300",
        render: (item) => {
          const pmId = item?.blContainer?.pmId || item?.pmId;
          const match = purchaseOptions.find((opt) => String(opt.value) === String(pmId));
          const dollarRate = match?.dollarRate || 0;
          const rs = (parseFloat(item.price) * parseFloat(item.netWeight) * dollarRate) / 1000;
          return formatCurrency(rs, "INR");
        },
      },
      {
        key: "status",
        label: "Status",
        render: (item) => {
          const { status } = getArrivedStatus(item);
          const isConverted = !!item.transactionDate;
          const isProcessing = !!item.isProcessing;

          return (
            <div className="flex flex-col gap-1">
              <span
                className={`inline-flex min-w-20 w-fit rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${isProcessing
                  ? "bg-primary-100 text-primary-700"
                  : isConverted
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                  }`}
              >
                {isProcessing
                  ? "Converted & Processed"
                  : isConverted
                    ? "Available"
                    : "Upcoming"}
              </span>
              {!isConverted && (
                <span
                  className={`text-[9px] font-bold italic ${status === "Arrived" || status === "Arrived ICD"
                    ? "text-emerald-600"
                    : "text-slate-400"
                    }`}
                >
                  BL: {String(status)}
                </span>
              )}
            </div>
          );
        },
      },
    ],
    [purchaseOptions, getArrivedStatus],
  );

  const handleConvertStock = async (values) => {
    if (!selectedStockForConvert) return;

    const lotLabel =
      lotOptions.find((l) => String(l.value) === String(values.lotNumber))
        ?.label || (typeof values.lotNumber === "object" ? "Selected Lot" : values.lotNumber);
    const yardLabel =
      yardOptions.find((y) => String(y.value) === String(values.yardId))
        ?.label || (typeof values.yardId === "object" ? "Selected Yard" : values.yardId);
    const categoryLabel =
      selectedStockForConvert?.stockCategory?.stockCategoryName ||
      selectedStockForConvert?.stockCategoryName ||
      "-";

    const weightLabel = selectedStockForConvert?.netWeight
      ? `${Number(selectedStockForConvert.netWeight).toLocaleString()} KG`
      : "0 KG";

    const isProcessing = Boolean(selectedStockForConvert?.isProcessing);
    const confirmText = isProcessing
      ? "Are you sure you want to update the Arrival Date for this processed stock?"
      : `Are you sure you want to ${isEditConvert ? "update" : "convert"} this to Available?`;

    const result = await alertService.confirm(
      `<div class="text-left space-y-3">
        <p>${confirmText}</p>
        <div class="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
          <p class="flex justify-between">
            <span class="font-bold text-slate-500 uppercase text-[10px]">Lot Number:</span>
            <span class="font-black text-primary-600">${String(lotLabel)}</span>
          </p>
          <p class="flex justify-between">
            <span class="font-bold text-slate-500 uppercase text-[10px]">Yard:</span>
            <span class="font-black text-emerald-600">${String(yardLabel)}</span>
          </p>
          <div class="flex justify-between">
            <p class="font-bold text-slate-500 uppercase text-[10px]">Stock Category:</p>
            <div class="font-black text-purple-600">${categoryLabel}</div>
          </div>
          <p class="flex justify-between">
            <span class="font-bold text-slate-500 uppercase text-[10px]">Weight:</span>
            <span class="font-black text-rose-500 font-mono">${String(weightLabel)}</span>
          </p>
        </div>
        ${!isProcessing
        ? `<p class="text-[11px] font-medium text-amber-600 italic">
                This will move it to stock addition and cannot be undone.
               </p>`
        : ""
      }
      </div>`,
    );
    if (!result.isConfirmed) return;

    try {
      const endpoint = isEditConvert
        ? "/container-stock-details/edit-stock-processing"
        : "/container-stock-details/move-to-stock-addition";

      await apiClient.post(endpoint, {
        blContainerStockDetailsId:
          selectedStockForConvert.blContainerStockDetailsId,
        lotNumber: String(values.lotNumber),
        yardId: Number(values.yardId),
        date: values.date,
      });
      alertService.success(
        isProcessing
          ? "Arrival Date updated successfully."
          : `${isEditConvert ? "Updated" : "Converted"} to Available successfully.`
      );
      setConvertModalOpen(false);
      refresh();
    } catch (err) {
      alertService.error(normalizeApiError(err).message);
    }
  };

  const openConvertModal = async (item) => {
    setSelectedStockForConvert(item);
    const isAlreadyConverted = item.isConverted || !!item.transactionDate;
    setIsEditConvert(isAlreadyConverted);

    if (isAlreadyConverted) {
      try {
        const response = await apiClient.get(
          `/container-stock-details/get-one/${item.blContainerStockDetailsId}`
        );
        const data = response?.data?.data || response?.data || item;
        setSelectedStockForConvert({
          ...item,
          ...data
        });
      } catch (err) {
        console.error("Failed to fetch conversion details", err);
      }
    }
    setConvertModalOpen(true);
  };

  const handleMoveToProcessing = async (item) => {
    const lotLabel = item.lotNo || "-";
    const yardLabel = item.yardName || "-";
    const categoryLabel = item.stockCategory?.stockCategoryName || item.stockCategoryName || "-";
    const weightLabel = item.netWeight
      ? `${Number(item.netWeight).toLocaleString()} KG`
      : "0 KG";

    const result = await alertService.confirm(
      `<div class="text-left space-y-3">
        <p>Are you sure you want to move this stock to <b>Processing</b>?</p>
        <div class="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
          <p class="flex justify-between">
            <span class="font-bold text-slate-500 uppercase text-[10px]">Lot Number:</span>
            <span class="font-black text-primary-600">${String(lotLabel)}</span>
          </p>
          <p class="flex justify-between">
            <span class="font-bold text-slate-500 uppercase text-[10px]">Yard:</span>
            <span class="font-black text-emerald-600">${String(yardLabel)}</span>
          </p>
          <p class="flex justify-between">
            <span class="font-bold text-slate-500 uppercase text-[10px]">Stock Category:</span>
            <span class="font-black text-purple-600">${String(categoryLabel)}</span>
          </p>
          <p class="flex justify-between">
            <span class="font-bold text-slate-500 uppercase text-[10px]">Weight:</span>
            <span class="font-black text-rose-500 font-mono">${String(weightLabel)}</span>
          </p>
        </div>
        <p class="text-[11px] font-medium text-rose-600 italic">
          Warning: Once moved to processing, you will not be able to edit or delete this conversion record again.
        </p>
      </div>`,
      {
        title: "Move to Processing",
        confirmButtonText: "Yes, Move to Processing",
        confirmButtonColor: "#10b981",
      }
    );

    if (!result.isConfirmed) return;

    try {
      await apiClient.post("/container-stock-details/move-to-stock-processing", {
        blContainerStockDetailsId: item.blContainerStockDetailsId,
      });
      alertService.success("Stock moved to processing successfully.");
      refresh();
    } catch (err) {
      alertService.error(normalizeApiError(err).message);
    }
  };

  const openViewDetail = async (item) => {
    setIsLoadingView(true);
    try {
      const [contRes, stockRes] = await Promise.all([
        apiClient.get(`/containers/${item.blContainerId}`),
        apiClient.get("/container-stock-details", {
          params: { blContainerId: item.blContainerId, limit: 1000, page: 1 },
        }),
      ]);
      const contData = contRes?.data?.data || contRes?.data || {};
      const stockDataArray =
        stockRes?.data?.data?.data || stockRes?.data?.data || [];

      // Find dollarRate from purchaseOptions
      const pmId = contData.pmId || item?.blContainer?.pmId || item?.pmId;
      const match = purchaseOptions.find((opt) => String(opt.value) === String(pmId));
      const dollarRate = match?.dollarRate || 0;
      const currencyCode = match?.currencyCode || "USD";

      setViewDetailData({ ...contData, dollarRate, currencyCode });
      setViewDetailStock(Array.isArray(stockDataArray) ? stockDataArray : []);
    } catch (err) {
      alertService.error("Failed to load details.");
    } finally {
      setIsLoadingView(false);
    }
  };

  const redirectToPurchase = (item) => {
    const pmId = item?.blContainer?.pmId || item?.pmId;
    if (!pmId) return alertService.error("Linked Purchase ID not found.");
    navigate(`/purchase-master/${encrypt(pmId)}/edit`);
  };

  const minArrivalDate = useMemo(() => {
    if (!selectedStockForConvert?.eta) return undefined;
    const etaDate = new Date(selectedStockForConvert.eta);
    if (Number.isNaN(etaDate.getTime())) return undefined;
    return etaDate.toISOString().split("T")[0];
  }, [selectedStockForConvert]);

  const dynamicConvertSchema = useMemo(() => {
    const isProcessing = Boolean(selectedStockForConvert?.isProcessing);
    return convertStockSchema.map((f) => {
      if (f.name === "lotNumber") {
        return {
          ...f,
          options: lotOptions,
          disabled: isProcessing,
          onAdd: () => setLotModalOpen(true)
        };
      }
      if (f.name === "yardId") {
        return {
          ...f,
          options: yardOptions,
          disabled: isProcessing,
          onAdd: () => setYardModalOpen(true)
        };
      }
      if (f.name === "date") {
        return {
          ...f,
          validation: {
            ...f.validation,
            minDate: minArrivalDate,
            minDateMessage: selectedStockForConvert?.eta
              ? `Arrival Date must be after ETA (${formatDate(selectedStockForConvert.eta)})`
              : undefined
          }
        };
      }
      return f;
    });
  }, [lotOptions, yardOptions, minArrivalDate, selectedStockForConvert]);

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        tableClassName="min-w-[1200px]"
        renderActions={(item) => {
          const { isArrived } = getArrivedStatus(item);
          const isAlreadyConverted = item.isConverted || !!item.transactionDate;
          const isProcessing = !!item.isProcessing;
          const isLocked = !!item.isLocked;

          let buttonLabel = "Convert";
          let buttonVariant = "primary";
          let isDisabled = false;

          if (isLocked) {
            buttonLabel = isProcessing ? "Processed" : isAlreadyConverted ? "Edit Convert" : "Convert";
            buttonVariant = isAlreadyConverted || isProcessing ? "secondary" : "primary";
            isDisabled = true;
          } else if (isProcessing) {
            buttonLabel = "Processed";
            buttonVariant = "secondary";
            isDisabled = false;
          } else if (isAlreadyConverted) {
            buttonLabel = "Edit Convert";
            buttonVariant = "secondary";
          } else if (!isArrived) {
            buttonLabel = "Not Available";
            isDisabled = true;
          }

          return (
            <div className="flex gap-2">
              <Button
                variant={buttonVariant}
                size="sm"
                className={`h-7 w-28 px-3 text-[10px] font-black uppercase whitespace-nowrap ${(isAlreadyConverted || isProcessing) && !isLocked
                  ? "border-primary-200 text-primary-700 hover:bg-primary-50"
                  : ""
                  }`}
                onClick={() => openConvertModal(item)}
                disabled={isDisabled}
                title={
                  isLocked
                    ? "Stock is locked and cannot be edited"
                    : isProcessing
                      ? "Edit arrival date for processed stock"
                      : !isArrived
                        ? "Container not yet arrived"
                        : isAlreadyConverted
                          ? "Edit conversion details"
                          : "Convert stock to available"
                }
              >
                {(isAlreadyConverted || isProcessing) && !isLocked && (
                  <svg className="mr-1 h-3.5 w-3.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                )}
                {buttonLabel}
              </Button>

              {isAlreadyConverted && !isProcessing && (
                <Button
                  variant="primary"
                  size="sm"
                  className="px-2.5 h-7 text-[10px] font-black uppercase bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm"
                  onClick={() => handleMoveToProcessing(item)}
                  title="Move to Stock Processing"
                >
                  <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Move to Process
                </Button>
              )}

              <Button
                variant="secondary"
                size="sm"
                className="p-0"
                onClick={() => openViewDetail(item)}
                title="View Container Details"
              >
                <svg className="h-4 w-4 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="px-2.5"
                onClick={() => redirectToPurchase(item)}
                disabled={isAlreadyConverted || isProcessing}
                title={isProcessing ? "Cannot edit processed stock" : isAlreadyConverted ? "Cannot edit converted stock" : "Edit Purchase"}
              >
                <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </Button>
              <Button
                variant="danger"
                size="sm"
                className="px-2.5"
                onClick={() => askDelete(item)}
                disabled={isAlreadyConverted || isProcessing}
                title={isProcessing ? "Cannot delete processed stock" : isAlreadyConverted ? "Cannot delete converted stock" : "Delete Item"}
              >
                <svg className="mr-1 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </Button>
            </div>
          );
        }}
        onEdit={null}
        onDelete={null}
        searchKey="search"
        searchPlaceholder="Search by Bill, Container or Category..."
        tableScrollHeight="58vh"
        toolbarFilters={
          <div className="flex gap-3">
            <div className="w-64">
              <SearchableDropdown
                placeholder="Filter by Category"
                options={[
                  { value: "", label: "All Categories" },
                  ...stockCategoryOptions,
                ]}
                value={filters.stockCategoryId || ""}
                onChange={(e) =>
                  updateFilters({ stockCategoryId: e.target.value, page: 1 })
                }
              />
            </div>
            {containerOptions.length > 0 && (
              <div className="w-64">
                <SearchableDropdown
                  placeholder="Filter by Container"
                  options={[
                    { value: "", label: "All Containers" },
                    ...containerOptions,
                  ]}
                  value={filters.blContainerId || ""}
                  onChange={(e) =>
                    updateFilters({ blContainerId: e.target.value, page: 1 })
                  }
                />
              </div>
            )}
          </div>
        }
        toolbarAction={null}
      />

      {/* Container Details Modal (View Detail) */}
      <Modal
        isOpen={Boolean(viewDetailData)}
        onClose={() => setViewDetailData(null)}
        title={
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 dark:bg-primary-500/20">
              <i className="material-icons text-xl">inventory_2</i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">
                Container <span className="text-primary-600">Information</span>
              </h3>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                Overview of physical assets and allocated stock
              </p>
            </div>
          </div>
        }
        maxWidth="max-w-3xl"
      >
        {isLoadingView ? (
          <div className="p-16 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-600"></div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fetching Container Data...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900/50 shadow-sm">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                <div className="flex flex-col gap-1">
                  <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">
                    Container No
                  </p>
                  <p className="text-base font-black text-slate-900 dark:text-white">
                    {viewDetailData?.containerNo}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">
                    Total Capacity
                  </p>
                  <p className="text-base font-black text-primary-600 font-mono">
                    {viewDetailData?.weight ? Number(viewDetailData.weight).toLocaleString() : "0"} <span className="text-[10px] font-bold text-slate-400 uppercase">KG</span>
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-[9px] text-slate-400 uppercase font-black tracking-widest">
                    Total Allocated
                  </p>
                  <p className="text-base font-black text-emerald-600 font-mono">
                    {viewDetailStock
                      .reduce(
                        (sum, it) => sum + (parseFloat(it.netWeight) || 0),
                        0,
                      )
                      .toLocaleString()}{" "}
                    <span className="text-[10px] font-bold text-slate-400 uppercase">KG</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest flex items-center gap-2">
                  <i className="material-icons text-sm">list_alt</i>
                  Allocated Stock Items
                </p>
                <span className="text-[9px] font-black text-slate-400 uppercase bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {viewDetailStock.length} Categories
                </span>
              </div>

              <div className="rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm min-w-[600px]">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] uppercase font-black tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Category</th>
                        <th className="px-4 py-3 text-center">Lot No</th>
                        <th className="px-4 py-3 text-center">Yard</th>
                        <th className="px-4 py-3 text-right">Net Weight</th>
                        <th className="px-4 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                      {viewDetailStock.map((s, idx) => (
                        <tr
                          key={s.blContainerStockDetailsId || idx}
                          className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors"
                        >
                          <td className="px-4 py-3 font-bold text-slate-800 dark:text-slate-200">
                            {(() => {
                              const raw = s.stockCategory?.stockCategoryName || s.stockCategoryName || "-";
                              const categories = String(raw)
                                .split(",")
                                .map((value) => value.trim())
                                .filter(Boolean);
                              if (categories.length === 0) return "-";
                              if (categories.length === 1) return categories[0];
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {categories.map((category, index) => (
                                    <span
                                      key={`${category}-${index}`}
                                      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                    >
                                      {category}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-[10px] font-black text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-100 dark:border-slate-700">
                              {s.lotNo || s.lot?.lotNo || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-[10px] font-bold text-slate-500 uppercase tracking-tight">
                            {s.yardName || s.yard?.yardNo || "-"}
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-black text-primary-600">
                            {Number(s.netWeight).toLocaleString()} <span className="text-[8px] font-normal text-slate-400">KG</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-tighter ${s.isProcessing
                                ? "bg-primary-50 text-primary-600 border border-primary-100 dark:bg-primary-900/20"
                                : s.transactionDate
                                  ? "bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-900/20"
                                  : "bg-amber-50 text-amber-600 border border-amber-100 dark:bg-amber-900/20"
                                }`}
                            >
                              {s.isProcessing ? "Processing" : s.transactionDate ? "In Stock" : "Pending"}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {viewDetailStock.length === 0 && (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-4 py-12 text-center text-[10px] font-bold uppercase tracking-widest text-slate-400 italic"
                          >
                            No physical stock allocated to this container.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-4">
              <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                Description / Remarks
              </p>
              <div className="text-[11px] leading-relaxed text-slate-600 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/30 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                {viewDetailData?.description || "No specific description provided."}
                <div className="my-2 h-[1px] bg-slate-100 dark:bg-slate-700 w-12"></div>
                <span className="italic font-medium">
                  {viewDetailData?.remarks || "No additional remarks."}
                </span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setViewDetailData(null)}
                className="px-6 h-10 uppercase text-[10px] font-black tracking-widest"
              >
                Close Details
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Convert Stock Modal */}
      <CrudModal
        isOpen={convertModalOpen}
        onClose={() => setConvertModalOpen(false)}
        mode={isEditConvert ? "edit" : "create"}
        title={selectedStockForConvert?.isProcessing ? "Edit Arrival Date" : isEditConvert ? "Edit Conversion Details" : "Convert Stock to Available"}
        closeOnOutsideClick={false}
        schema={dynamicConvertSchema}
        initialValues={
          isEditConvert && selectedStockForConvert
            ? {
              lotNumber: selectedStockForConvert.lotId || "",
              date: selectedStockForConvert.transactionDate?.split("T")[0] || new Date().toISOString().split("T")[0],
              yardId: selectedStockForConvert.yardId || "",
            }
            : {
              date: (() => {
                const todayStr = new Date().toISOString().split("T")[0];
                if (!minArrivalDate) return todayStr;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const minDate = new Date(minArrivalDate);
                minDate.setHours(0, 0, 0, 0);
                return today < minDate ? minArrivalDate : todayStr;
              })(),
              yardId: ""
            }
        }
        isSubmitting={false}
        onSubmit={handleConvertStock}
      />

      <LotCreateModal
        isOpen={lotModalOpen}
        onClose={() => setLotModalOpen(false)}
        onSuccess={loadOptions}
      />

      <YardCreateModal
        isOpen={yardModalOpen}
        onClose={() => setYardModalOpen(false)}
        onSuccess={loadOptions}
      />
    </div>
  );
}

export default BLContainerModulePage;
